import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Compass, Heart, Shield, Target, ArrowRight } from 'lucide-react';
import { CommitmentLadder } from '@/components/shared/CommitmentLadder';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useRegionalPageContent } from '@/hooks/useRegionalPageContent';
import { LazyImage } from '@/components/ui/LazyImage';
import { IndividualIntroForm } from '@/components/forms/IndividualIntroForm';

export function ForIndividualsPage() {
  const { content: regionalContent } = useRegionalPageContent('individuals');
  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>Enneagram Coaching for Individuals | Personal Development | FIVE LENS</title>
        <meta name="description" content="Discover your core behavioural drives, blind spots, and growth path. Book a one-on-one FIVE LENS Enneagram debrief with an accredited coach." />
        <meta property="og:title" content="Enneagram Coaching for Individuals | Personal Development | FIVE LENS" />
        <meta property="og:description" content="Discover your core behavioural drives, blind spots, and growth path. Book a one-on-one FIVE LENS Enneagram debrief with an accredited coach." />
        <meta name="twitter:title" content="Enneagram Coaching for Individuals | Personal Development | FIVE LENS" />
        <meta name="twitter:description" content="Discover your core behavioural drives, blind spots, and growth path. Book a one-on-one FIVE LENS Enneagram debrief with an accredited coach." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
        <Breadcrumb items={[{ label: 'For Individuals' }]} />
        
        <div className="grid lg:grid-cols-2 gap-16 mt-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-serif text-ink mb-6 leading-tight">
              {regionalContent?.title || 'Unlock Your Personal Mastery'}
            </h1>
            {regionalContent?.htmlContent ? (
              <div 
                className="prose prose-brand max-w-none prose-p:text-xl prose-p:text-ink/70/80 prose-p:leading-relaxed prose-p:font-light prose-p:mb-8"
                dangerouslySetInnerHTML={{ __html: regionalContent.htmlContent }}
              />
            ) : (
              <p className="text-xl text-ink/70/80 leading-relaxed mb-8 font-light">
                Deepen your self-awareness, transform your leadership, and build emotional resilience with our multidimensional FIVE LENS approach. From personal mastery to 1-on-1 executive coaching, uncover the root behavioral drivers that shape your impact.
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/personal-mastery-and-resilience" 
                className="inline-flex items-center justify-center bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/70 transition-colors"
              >
                Explore the Report
              </Link>
              <a 
                href="#request-intro" 
                className="inline-flex items-center justify-center border border-ink text-ink px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ground-alt transition-colors"
              >
                Request a FIVE LENS Enneagram Debrief
              </a>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[4/3] bg-white p-2 border border-ground-deep shadow-xl">
              <LazyImage 
                src="/five_lens_workplace.webp" 
                alt="FIVE LENS workplace collaboration and coaching" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-rule-3 text-white p-6 md:p-8 max-w-[240px] shadow-lg">
              <p className="font-serif text-xl italic leading-snug">"The journey of a thousand miles begins with a single step."</p>
            </div>
          </motion.div>
        </div>

        {/* Coach Intro Request Form Section */}
        <section id="request-intro" className="mt-24 scroll-mt-28">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-serif text-ink mb-4">
                Request a FIVE LENS Enneagram Debrief
              </h2>
              <p className="text-base text-ink/70 leading-relaxed font-light">
                Looking to experience a personal debrief or partner with an accredited FIVE LENS coach for personal mastery? Tell us what you're working toward, and our team will connect you with an accredited practitioner for your 1-on-1 debrief.
              </p>
            </div>
            <IndividualIntroForm />
          </div>
        </section>
      </div>

      <section className="bg-white py-24 border-y border-ground-deep">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-serif text-ink mb-6">Why FIVE LENS for Individuals?</h2>
            <p className="text-lg text-ink/70/80 font-light">
              Unlike traditional Enneagram assessments, we look at the whole person through five distinct lenses, giving you actionable insights for personal growth, leadership, and high-impact coaching.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Target,
                title: 'Executive Coaching',
                desc: 'Targeted 1-on-1 executive coaching for leaders and professionals looking to sharpen executive presence, navigate organizational complexity, and break through behavioral plateaus.'
              },
              {
                icon: Compass,
                title: 'Deep Self-Awareness',
                desc: 'Uncover your hidden drivers, blind spots, and natural strengths across all five lenses of human behavior.'
              },
              {
                icon: Heart,
                title: 'Better Relationships',
                desc: 'Understand how you interact with others, improve your communication, and build deeper empathy and trust.'
              },
              {
                icon: Shield,
                title: 'Emotional Resilience',
                desc: 'Learn practical strategies to manage stress, regulate your emotions under pressure, and bounce back from adversity.'
              }
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="p-8 border border-ground-deep bg-ground-alt hover:shadow-md hover:shadow-ink/5 hover:border-ink hover:-translate-y-1 transition-all group">
                  <Icon className="w-8 h-8 text-rule-3 mb-6 group-hover:scale-110 transition-transform" />
                  <h3 className="text-xl font-serif text-ink mb-4">{feature.title}</h3>
                  <p className="text-ink/70/70 leading-relaxed text-sm">
                    {feature.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-ink text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-4xl font-serif mb-6">Start Your Journey Today</h2>
          <p className="text-lg text-ground-deep/80 mb-10 font-light">
            Whether you want to understand yourself better, elevate your career, or partner with an accredited executive coach, we have the resources to guide you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/personal-mastery-and-resilience" 
              className="inline-flex items-center justify-center bg-white text-ink px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ground-deep transition-colors"
            >
              Get Your Report
            </Link>
            <a 
              href="#request-intro" 
              className="inline-flex items-center justify-center border border-white text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-white/10 transition-colors"
            >
              Request a FIVE LENS Enneagram Debrief
            </a>
          </div>
        </div>
      </section>

      <CommitmentLadder context="general" />
    </main>
  );
}
